// =====================================================================
//  ADMIN CONSOLE, PERFORMANCE
// ---------------------------------------------------------------------
//  Review records and the rating distribution behind them.
//
//  The distribution is the point of this screen. A single average tells an
//  administrator almost nothing; the shape of the curve tells them whether
//  reviews are being used honestly or everyone is being rated a 4.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, BarChart, Cell, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Download, Pencil, Plus, Star, Target, TrendingUp, Users } from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  ChartTooltip,
  Chip,
  Drawer,
  EmptyState,
  Field,
  FilterChip,
  Grid,
  KeyValue,
  KpiTile,
  Modal,
  PageHeader,
  Panel,
  PanelBody,
  PanelHead,
  PersonCell,
  SearchInput,
  Select,
  StatusChip,
  Textarea,
  Toolbar,
  ToolbarSpacer,
  Input,
  toast,
  useDebounced,
} from '../ui';
import { series, state } from '../theme';
import { DASH, distinct, downloadCsv, fmtDate, fmtNumber, matchesSearch, orDash, toISODate } from '../format';

const BAND_COLORS = {
  Outstanding: state.positive.solid,
  Exceeds: series.accent,
  Meets: state.warning.solid,
  Below: state.negative.solid,
};

const STATUSES = ['Completed', 'In Progress', 'Draft'];

const EMPTY = {
  employee_id: '',
  employee_name: '',
  review_period: '',
  overall_rating: '',
  goals_rating: '',
  skills_rating: '',
  strengths: '',
  areas_for_improvement: '',
  goals: '',
  reviewer: '',
  status: 'Completed',
};

/** Star row for a 0,5 rating. */
function Stars({ value }) {
  const rating = Number(value) || 0;
  if (!rating) return <span style={{ color: 'var(--ac-faint)' }}>{DASH}</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ display: 'inline-flex', gap: 1 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={11}
            strokeWidth={2}
            fill={i <= Math.round(rating) ? state.warning.solid : 'none'}
            color={i <= Math.round(rating) ? state.warning.solid : 'var(--ac-line)'}
          />
        ))}
      </span>
      <span className="ac-num" style={{ fontWeight: 650, fontSize: 12 }}>
        {rating.toFixed(1)}
      </span>
    </span>
  );
}

export default function PerformancePage({ role, currentUser }) {
  const queryClient = useQueryClient();
  const canManage = ['admin', 'hr', 'manager'].includes(role);

  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [active, setActive] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  const debouncedSearch = useDebounced(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'performance'],
    queryFn: () => backendApi.entities.Performance.page({ limit: 500, sort: 'created_at', order: 'desc' }),
  });

  // Pinned identity so the memos below don't recompute on every render.
  const reviews = useMemo(() => data?.rows || [], [data]);

  const { data: snapshot } = useQuery({
    queryKey: ['admin', 'performance-snapshot'],
    queryFn: async () => (await backendApi.admin.overview())?.performance || null,
  });

  const { data: employees } = useQuery({
    queryKey: ['admin', 'people-lite'],
    queryFn: () => backendApi.entities.Employee.page({ limit: 1000, sort: 'full_name', order: 'asc' }),
    enabled: formOpen,
  });

  const employeeOptions = (employees?.rows || []).map((e) => ({
    value: String(e.id),
    label: `${e.full_name}${e.department ? ` · ${e.department}` : ''}`,
  }));

  /* ------------------------------- form ------------------------------- */
  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, reviewer: currentUser?.full_name || '' });
    setErrors({});
    setFormOpen(true);
  };

  const openEdit = (review) => {
    setEditing(review);
    setForm({
      employee_id: review.employee_id ? String(review.employee_id) : '',
      employee_name: review.employee_name || '',
      review_period: review.review_period || '',
      overall_rating: review.overall_rating ?? '',
      goals_rating: review.goals_rating ?? '',
      skills_rating: review.skills_rating ?? '',
      strengths: review.strengths || '',
      areas_for_improvement: review.areas_for_improvement || '',
      goals: review.goals || '',
      reviewer: review.reviewer || '',
      status: review.status || 'Completed',
    });
    setErrors({});
    setFormOpen(true);
  };

  const setField = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Keep the denormalised name column in step with the selected person,
      // since the table stores both an id and a name.
      if (key === 'employee_id') {
        const match = (employees?.rows || []).find((e) => String(e.id) === String(value));
        if (match) next.employee_name = match.full_name;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const save = useMutation({
    mutationFn: (payload) =>
      editing
        ? backendApi.entities.Performance.update(editing.id, payload)
        : backendApi.entities.Performance.create(payload),
    onSuccess: () => {
      toast.success(editing ? 'Review updated' : 'Review recorded');
      setFormOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(err.message || 'Could not save this review'),
  });

  const submit = (event) => {
    event.preventDefault();
    const next = {};
    if (!form.employee_id) next.employee_id = 'Select an employee';
    if (!form.review_period.trim()) next.review_period = 'Review period is required';
    ['overall_rating', 'goals_rating', 'skills_rating'].forEach((key) => {
      const value = form[key];
      if (value !== '' && (Number.isNaN(Number(value)) || Number(value) < 0 || Number(value) > 5)) {
        next[key] = 'Must be between 0 and 5';
      }
    });
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    save.mutate(
      Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '' && v !== null && v !== undefined))
    );
  };

  /* --------------------------- derived views -------------------------- */
  const periods = useMemo(() => distinct(reviews, 'review_period'), [reviews]);

  const filtered = useMemo(
    () =>
      reviews.filter(
        (row) =>
          matchesSearch(row, debouncedSearch, ['employee_name', 'review_period', 'reviewer', 'strengths']) &&
          (!periodFilter || row.review_period === periodFilter) &&
          (!statusFilter || String(row.status).toLowerCase() === statusFilter.toLowerCase())
      ),
    [reviews, debouncedSearch, periodFilter, statusFilter]
  );

  const topPerformers = useMemo(
    () =>
      [...reviews]
        .filter((r) => Number(r.overall_rating) > 0)
        .sort((a, b) => Number(b.overall_rating) - Number(a.overall_rating))
        .slice(0, 6),
    [reviews]
  );

  const columns = useMemo(
    () => [
      {
        key: 'employee_name',
        label: 'Employee',
        width: 220,
        render: (row) => <PersonCell name={row.employee_name} sub={row.review_period} />,
      },
      {
        key: 'review_period',
        label: 'Period',
        width: 140,
        render: (row) => (row.review_period ? <Chip>{row.review_period}</Chip> : DASH),
      },
      {
        key: 'overall_rating',
        label: 'Overall',
        width: 130,
        render: (row) => <Stars value={row.overall_rating} />,
        csv: (row) => row.overall_rating,
      },
      {
        key: 'goals_rating',
        label: 'Goals',
        width: 80,
        align: 'right',
        render: (row) => (row.goals_rating ? Number(row.goals_rating).toFixed(1) : DASH),
        csv: (row) => row.goals_rating,
      },
      {
        key: 'skills_rating',
        label: 'Skills',
        width: 80,
        align: 'right',
        render: (row) => (row.skills_rating ? Number(row.skills_rating).toFixed(1) : DASH),
        csv: (row) => row.skills_rating,
      },
      { key: 'reviewer', label: 'Reviewer', width: 150, render: (row) => orDash(row.reviewer) },
      { key: 'status', label: 'Status', width: 115, render: (row) => <StatusChip value={row.status} /> },
      { key: 'created_at', label: 'Recorded', width: 115, render: (row) => fmtDate(row.created_at) },
    ],
    []
  );

  const exportCsv = () => {
    downloadCsv(`performance-${toISODate()}`, filtered, [
      { key: 'employee_name', label: 'Employee' },
      { key: 'review_period', label: 'Period' },
      { key: 'overall_rating', label: 'Overall' },
      { key: 'goals_rating', label: 'Goals' },
      { key: 'skills_rating', label: 'Skills' },
      { key: 'reviewer', label: 'Reviewer' },
      { key: 'status', label: 'Status' },
      { key: 'strengths', label: 'Strengths' },
      { key: 'areas_for_improvement', label: 'Areas for improvement' },
    ]);
    toast.success(`Exported ${filtered.length} reviews`);
  };

  return (
    <div className="ac-page ac-page--wide">
      <PageHeader
        title="Performance"
        sub="Review records, rating distribution, and who is standing out."
        actions={
          <>
            <Button icon={Download} onClick={exportCsv} disabled={filtered.length === 0}>
              Export
            </Button>
            {canManage ? (
              <Button variant="primary" icon={Plus} onClick={openCreate}>
                Record review
              </Button>
            ) : null}
          </>
        }
      />

      {snapshot ? (
        <Grid cols={4} style={{ marginBottom: 14 }}>
          <KpiTile label="Reviews" value={fmtNumber(snapshot.reviews)} icon={Target} foot="All time" />
          <KpiTile
            label="Average rating"
            value={snapshot.averageRating || DASH}
            unit="/ 5"
            icon={Star}
            tone={snapshot.averageRating >= 3.5 ? 'pos' : 'warn'}
            foot="Across all reviews"
          />
          <KpiTile
            label="Completed"
            value={fmtNumber(snapshot.completed)}
            icon={TrendingUp}
            tone="pos"
            foot={`${snapshot.inProgress} still open`}
          />
          <KpiTile
            label="Needs attention"
            value={fmtNumber(snapshot.distribution.find((d) => d.band === 'Below')?.count || 0)}
            icon={Users}
            tone="neg"
            foot="Rated under 2.5"
          />
        </Grid>
      ) : null}

      <Grid cols={2} style={{ marginBottom: 14 }}>
        <Panel>
          <PanelHead title="Rating distribution" sub="How reviews are spread across performance bands" />
          <PanelBody>
            {!snapshot || snapshot.reviews === 0 ? (
              <EmptyState icon={Target} title="No reviews yet" text="Record a review to see the distribution." />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={snapshot.distribution} margin={{ top: 6, right: 6, left: -22, bottom: 0 }} barCategoryGap="32%">
                  <CartesianGrid strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="band" tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} width={44} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(15,20,25,0.04)' }} />
                  <Bar dataKey="count" name="Reviews" radius={[3, 3, 0, 0]}>
                    {snapshot.distribution.map((entry) => (
                      <Cell key={entry.band} fill={BAND_COLORS[entry.band] || series.slate} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Highest rated" sub="Top overall ratings on record" />
          <PanelBody flush>
            {topPerformers.length === 0 ? (
              <EmptyState icon={Star} title="No ratings yet" />
            ) : (
              <div className="ac-feed">
                {topPerformers.map((r) => (
                  <div className="ac-feed-item" key={r.id}>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                      <div className="ac-feed-title">{r.employee_name}</div>
                      <div className="ac-feed-meta">
                        {r.review_period || 'No period'}
                        {r.reviewer ? ` · reviewed by ${r.reviewer}` : ''}
                      </div>
                    </div>
                    <Stars value={r.overall_rating} />
                  </div>
                ))}
              </div>
            )}
          </PanelBody>
        </Panel>
      </Grid>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        onRowClick={setActive}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        emptyIcon={Target}
        emptyTitle={reviews.length === 0 ? 'No reviews recorded' : 'No matching reviews'}
        emptyText={
          reviews.length === 0
            ? 'Record a performance review to start tracking how the team is doing.'
            : 'Adjust the search or filters above.'
        }
        rowActions={canManage ? (row) => (
          <Button size="sm" variant="ghost" icon={Pencil} onClick={() => openEdit(row)} aria-label="Edit review" />
        ) : undefined}
        toolbar={
          <Toolbar>
            <SearchInput value={search} onChange={setSearch} placeholder="Search employee or reviewer…" width={240} />
            <FilterChip label="Period" value={periodFilter} options={periods} onChange={setPeriodFilter} />
            <FilterChip label="Status" value={statusFilter} options={STATUSES} onChange={setStatusFilter} />
            <ToolbarSpacer />
            <span style={{ fontSize: 11.5, color: 'var(--ac-faint)' }}>{fmtNumber(filtered.length)} reviews</span>
          </Toolbar>
        }
      />

      {/* --------------------------- DRAWER --------------------------- */}
      <Drawer
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.employee_name || 'Review'}
        sub={active?.review_period}
        badge={active ? <StatusChip value={active.status} /> : null}
        footer={
          <>
            <div style={{ flex: '1 1 auto' }} />
            <Button onClick={() => setActive(null)}>Close</Button>
            {canManage ? (
              <Button
                variant="primary"
                icon={Pencil}
                onClick={() => {
                  const record = active;
                  setActive(null);
                  openEdit(record);
                }}
              >
                Edit
              </Button>
            ) : null}
          </>
        }
      >
        {active ? (
          <>
            <div className="ac-eyebrow" style={{ marginBottom: 7 }}>
              Ratings
            </div>
            <KeyValue
              items={[
                { key: 'Overall', value: <Stars value={active.overall_rating} /> },
                { key: 'Goals', value: <Stars value={active.goals_rating} /> },
                { key: 'Skills', value: <Stars value={active.skills_rating} /> },
                { key: 'Reviewer', value: active.reviewer },
                { key: 'Period', value: active.review_period },
                { key: 'Recorded', value: fmtDate(active.created_at) },
              ]}
            />

            {active.strengths ? (
              <>
                <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                  Strengths
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ac-body)' }}>{active.strengths}</p>
              </>
            ) : null}

            {active.areas_for_improvement ? (
              <>
                <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                  Areas for improvement
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ac-body)' }}>
                  {active.areas_for_improvement}
                </p>
              </>
            ) : null}

            {active.goals ? (
              <>
                <div className="ac-eyebrow" style={{ margin: '20px 0 7px' }}>
                  Goals
                </div>
                <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ac-body)' }}>{active.goals}</p>
              </>
            ) : null}
          </>
        ) : null}
      </Drawer>

      {/* ---------------------------- FORM ---------------------------- */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'Edit review' : 'Record a performance review'}
        size="lg"
        footer={
          <>
            <Button onClick={() => setFormOpen(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {editing ? 'Save changes' : 'Record review'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit}>
          <Grid cols={2}>
            <Field label="Employee" required error={errors.employee_id}>
              <Select
                value={form.employee_id}
                onChange={setField('employee_id')}
                options={employeeOptions}
                placeholder="Select an employee"
                invalid={!!errors.employee_id}
                disabled={Boolean(editing)}
              />
            </Field>
            <Field label="Review period" required error={errors.review_period} hint="e.g. Q1 2026 or H1 2026">
              <Input value={form.review_period} onChange={setField('review_period')} invalid={!!errors.review_period} />
            </Field>
            <Field label="Overall rating" error={errors.overall_rating} hint="0, 5">
              <Input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={form.overall_rating}
                onChange={setField('overall_rating')}
                invalid={!!errors.overall_rating}
              />
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={setField('status')} options={STATUSES} />
            </Field>
            <Field label="Goals rating" error={errors.goals_rating}>
              <Input type="number" min="0" max="5" step="0.1" value={form.goals_rating} onChange={setField('goals_rating')} />
            </Field>
            <Field label="Skills rating" error={errors.skills_rating}>
              <Input type="number" min="0" max="5" step="0.1" value={form.skills_rating} onChange={setField('skills_rating')} />
            </Field>
            <Field label="Reviewer">
              <Input value={form.reviewer} onChange={setField('reviewer')} />
            </Field>
          </Grid>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
            <Field label="Strengths">
              <Textarea value={form.strengths} onChange={setField('strengths')} rows={3} />
            </Field>
            <Field label="Areas for improvement">
              <Textarea value={form.areas_for_improvement} onChange={setField('areas_for_improvement')} rows={3} />
            </Field>
            <Field label="Goals for next period">
              <Textarea value={form.goals} onChange={setField('goals')} rows={3} />
            </Field>
          </div>
        </form>
      </Modal>
    </div>
  );
}
